import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS, BUTTON_SIZES } from "@shared/ui/Button";
import {
  DropdownMenuContent,
  DropdownMenuProvider,
  DropdownMenuTrigger,
  useDropdownMenu,
} from "@shared/ui/DropdownMenu";
import { Checkbox, CHECKBOX_SIZES } from "@shared/ui/Inputs";
import Tooltip from "@shared/ui/Tooltip";
import { CaretDown, CaretRight, StackSimple } from "phosphor-react";
import React, { useState, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";

import { DownAngleSmallIcon } from "@shared/ui/DownAngleSmallIcon";

const buildTree = (flatList) => {
  if (!flatList || flatList.length === 0) return [];

  const root = { children: [] };
  const stack = [{ node: root, level: -1 }];

  flatList.forEach((item) => {
    const node = { ...item, children: [] };

    while (stack.length > 1 && stack[stack.length - 1].level >= item.level) {
      stack.pop();
    }

    const parent = stack[stack.length - 1].node;
    parent.children.push(node);
    stack.push({ node, level: item.level });
  });

  return root.children;
};

const HierarchyItem = ({
  node,
  level,
  expandedIds,
  selectedIds,
  onToggleExpand,
  onToggleSelect,
  currentAssetId,
  lockedTooltipId,
  lockedTooltipMsg,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isCurrentAsset = node.id === currentAssetId;
  const isSelected = isCurrentAsset || selectedIds.has(node.id);

  return (
    <div className="flex flex-col w-full">
      <div
        className="flex items-center w-full hover:bg-neutral-50 py-1 pr-2 rounded transition-colors"
        style={{ paddingLeft: `${level * 24 + 4}px` }}
      >
        <Button
          variant={BUTTON_VARIANTS.LINK}
          size={BUTTON_SIZES.SMALL}
          text={
            hasChildren ? (
              isExpanded ? (
                <CaretDown size={14} className="text-gray-500" />
              ) : (
                <CaretRight size={14} className="text-gray-500" />
              )
            ) : (
              ""
            )
          }
          className="w-5 h-5 flex-shrink-0 flex items-center justify-center mr-1"
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) onToggleExpand(node.id);
          }}
          tabIndex={hasChildren ? 0 : -1}
          style={{ cursor: hasChildren ? "pointer" : "default" }}
        />

        <div className="flex-shrink-0 flex items-center justify-center mr-1.5 text-gray-400">
          <StackSimple size={16} />
        </div>

        <BodyText
          size={BODY_TEXT_SIZES.SMALL}
          className="flex-1 min-w-0 font-medium text-gray-700 truncate cursor-default select-none leading-5"
        >
          {node.label}
        </BodyText>

        <div
          className="ml-2 flex-shrink-0 flex items-center"
          data-tip={isCurrentAsset ? lockedTooltipMsg : undefined}
          data-for={isCurrentAsset ? lockedTooltipId : undefined}
        >
          <Checkbox
            checked={isSelected}
            size={CHECKBOX_SIZES.SMALL}
            disabled={isCurrentAsset}
            onClick={(e) => {
              e.stopPropagation();
              if (!isCurrentAsset) {
                onToggleSelect(node.id);
              }
            }}
          />
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className="flex flex-col w-full">
          {node.children.map((child) => (
            <HierarchyItem
              key={child.id}
              node={child}
              level={level + 1}
              expandedIds={expandedIds}
              selectedIds={selectedIds}
              onToggleExpand={onToggleExpand}
              onToggleSelect={onToggleSelect}
              currentAssetId={currentAssetId}
              lockedTooltipId={lockedTooltipId}
              lockedTooltipMsg={lockedTooltipMsg}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DropdownContentWrapper = ({
  messages,
  treeData,
  expandedIds,
  selectedIds,
  initialSelectedIds,
  handleToggleExpand,
  handleToggleSelect,
  currentAssetId,
  lockedTooltipId,
  lockedTooltipMsg,
  onUpdateClick,
  handleSelectAll,
  assetListLength,
}) => {
  const { close } = useDropdownMenu();

  // Determine if there are changes
  const hasChanges = useMemo(() => {
    if (selectedIds.size !== initialSelectedIds.length) return true;
    for (const id of initialSelectedIds) {
      if (!selectedIds.has(id)) return true;
    }
    return false;
  }, [selectedIds, initialSelectedIds]);

  const handleUpdate = () => {
    onUpdateClick();
    close();
  };

  return (
    <DropdownMenuContent
      className="max-w-[320px] p-0"
      align="end"
      side="bottom"
    >
      <div className="p-4 border-b border-gray-5">
        <p className="mb-2 text-gray-500 text-sm">
          {messages["hierarchy"]["assets"]["selectAssetsDialog"]["helperText"]}
        </p>

        <div className="max-h-[300px] overflow-y-auto mt-2 custom-scrollbar">
          {treeData.map((node) => (
            <HierarchyItem
              key={node.id}
              node={node}
              level={0}
              expandedIds={expandedIds}
              selectedIds={selectedIds}
              onToggleExpand={handleToggleExpand}
              onToggleSelect={handleToggleSelect}
              currentAssetId={currentAssetId}
              lockedTooltipId={lockedTooltipId}
              lockedTooltipMsg={lockedTooltipMsg}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between p-4 bg-white">
        <Button
          className={`text-sm font-medium transition-colors ${
            assetListLength === 1 && selectedIds.has(currentAssetId)
              ? "text-gray-400 cursor-not-allowed"
              : "text-primary-600 hover:text-primary-700 cursor-pointer"
          }`}
          onClick={handleSelectAll}
          variant={BUTTON_VARIANTS.LINK}
          disabled={assetListLength === 1 && selectedIds.has(currentAssetId)}
          text={
            messages["hierarchy"]["assets"]["selectAssetsDialog"][
              selectedIds.size === assetListLength ? "deselectAll" : "selectAll"
            ]
          }
        />
        <Button
          className={`text-sm font-medium transition-colors ${
            hasChanges
              ? "text-primary-600 hover:text-primary-700 cursor-pointer"
              : "text-gray-400 cursor-not-allowed"
          }`}
          onClick={hasChanges ? handleUpdate : undefined}
          disabled={!hasChanges}
          text={messages["hierarchy"]["assets"]["selectAssetsDialog"]["update"]}
          variant={BUTTON_VARIANTS.LINK}
        />
      </div>
      <Tooltip id={lockedTooltipId} place="left" />
    </DropdownMenuContent>
  );
};

export const AssetHierarchySelect = ({
  assetList,
  onUpdate,
  currentAssetId,
  initialSelectedIds = [],
}) => {
  const { messages } = useIntl();
  const [treeData, setTreeData] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set(initialSelectedIds));

  const LOCKED_TOOLTIP_ID = "hierarchy-locked-tooltip";
  const lockedTooltipMsg =
    messages["hierarchy"]["assets"]["selectAssetsDialog"]["cantUnselect"];

  useEffect(() => {
    const tree = buildTree(assetList);
    setTreeData(tree);

    if (assetList.length > 0) {
      const allIds = assetList.map((node) => node.id);
      setExpandedIds(new Set(allIds));
    }
  }, [assetList]);

  useEffect(() => {
    if (initialSelectedIds.length > 0) {
      setSelectedIds(() => {
        return new Set(initialSelectedIds);
      });
    }
  }, [initialSelectedIds]);

  useEffect(() => {
    if (currentAssetId) {
      setSelectedIds((prev) => {
        const newSet = new Set(prev);
        newSet.add(currentAssetId);
        return newSet;
      });
    }
  }, [currentAssetId]);

  const handleToggleExpand = (id) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const handleToggleSelect = (id) => {
    if (id === currentAssetId) return;
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = (e) => {
    e.preventDefault();
    if (selectedIds.size === assetList.length) {
      const newSet = new Set();
      if (currentAssetId) newSet.add(currentAssetId);
      setSelectedIds(newSet);
    } else {
      const allIds = assetList.map((n) => n.id);
      setSelectedIds(new Set(allIds));
    }
  };

  const handleUpdateClick = () => {
    if (onUpdate) {
      onUpdate(Array.from(selectedIds));
    }
  };

  const hasActiveFilters =
    initialSelectedIds.length > 1 ||
    (initialSelectedIds.length === 1 &&
      !initialSelectedIds.includes(currentAssetId));

  return (
    <DropdownMenuProvider>
      <DropdownMenuTrigger asChild>
        <div className="relative inline-block cursor-pointer">
          <Button
            variant={BUTTON_VARIANTS.OUTLINE}
            size={BUTTON_SIZES.SMALL}
            className="pointer-events-none"
            text={
              messages["hierarchy"]["assets"]["selectAssetsDialog"]["title"]
            }
            leadingIcon={<DownAngleSmallIcon />}
          />
          {hasActiveFilters && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-danger rounded-full border border-white pointer-events-none" />
          )}
        </div>
      </DropdownMenuTrigger>

      <DropdownContentWrapper
        messages={messages}
        treeData={treeData}
        expandedIds={expandedIds}
        selectedIds={selectedIds}
        initialSelectedIds={initialSelectedIds}
        handleToggleExpand={handleToggleExpand}
        handleToggleSelect={handleToggleSelect}
        currentAssetId={currentAssetId}
        lockedTooltipId={LOCKED_TOOLTIP_ID}
        lockedTooltipMsg={lockedTooltipMsg}
        onUpdateClick={handleUpdateClick}
        handleSelectAll={handleSelectAll}
        assetListLength={assetList.length}
      />
    </DropdownMenuProvider>
  );
};
